import { useMutation, type UseMutationOptions } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

type BlacklistSiteIdentifierResponse = {
  code: string;
  message: string;
  requestId: UUID | undefined;
};

type BlacklistSiteIdentifierData = {
  blacklisted: boolean;
};

type BlacklistSiteIdentifierArguments = {
  identifierUuid: UUID;
  siteData: BlacklistSiteIdentifierData;
  siteUuid: UUID;
};

type BlacklistSiteIdentifierMutationOptions = UseMutationOptions<
  BlacklistSiteIdentifierResponse,
  Error,
  BlacklistSiteIdentifierArguments
>;

/**
 * Blacklists / un-blacklists a given site identifier for a given site.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Site/operation/blacklist_site_identifier_site__site_uuid___identifier_uuid__put
 *
 * @param {string} siteId - The ID of the site.
 * @param {IdentifierForSupplierData} siteData - Data related to the site identifier.
 * @return {Promise<BlacklistSiteIdentifierResponse>} The response of the PUT request.
 */
export const blacklistSiteIdentifier = async ({
  identifierUuid,
  siteUuid,
  siteData,
}: BlacklistSiteIdentifierArguments) => {
  if (!siteUuid) {
    throw new Error('No site UUID provided');
  }

  const { blacklisted } = siteData;
  const searchParams = {
    blacklisted,
  };

  const response = await vestigasApi
    .put(ENDPOINT.SITE.BLACKLIST_SITE_IDENTIFIER(siteUuid, identifierUuid), {
      searchParams,
    })
    .json<BlacklistSiteIdentifierResponse>();

  return response;
};

/**
 * Custom hook for blacklisting a site identifier using mutations.
 *
 * @param {Object} options - Additional options for the mutation.
 * @returns {Object} An object containing the mutation function and related properties.
 */
export const useMutationAddBlacklistSiteIdentifier = (
  options?: BlacklistSiteIdentifierMutationOptions,
) =>
  useMutation({
    mutationFn: async ({
      identifierUuid,
      siteUuid,
      siteData,
    }: BlacklistSiteIdentifierArguments) =>
      blacklistSiteIdentifier({ identifierUuid, siteUuid, siteData }),
    ...options,
  });
